import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { Button, Avatar } from '@components/common'
import './EditFavoritesModal.css'
import { IFavoritedUser } from '@dataTypes/user-favorite.type'
import { getFullName } from '@utils/fullname.util'
import { addFavoritedUsers, bulkDeleteUsers } from '@store/actions'
import { useAppDispatch } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'

type EditFavoritesModalProps = {
  isOpen: boolean
  onRequestClose: () => void
  availableMembers: IFavoritedUser[]
  currentFavorites: IFavoritedUser[]
}

const EDIT_FAVORITES_MODAL_LOADING_CONTAINER =
  '.edit-favorites-modal .modal-content'

const EditFavoritesModal: React.FC<EditFavoritesModalProps> = ({
  isOpen,
  onRequestClose,
  availableMembers,
  currentFavorites,
}) => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [searchValue, setSearchValue] = useState('')
  const [updatedMembers, setUpdatedMembers] =
    useState<IFavoritedUser[]>(currentFavorites)

  useEffect(() => {
    if (isOpen) {
      setUpdatedMembers(currentFavorites)
      setSearchValue('')
    }
  }, [isOpen, currentFavorites])

  const getUserFullName = (member: IFavoritedUser): string => {
    return `${member.provider_profile ? 'Dr. ' : ''}${getFullName(member)}`
  }

  const filteredMembers = availableMembers.filter(
    (member) =>
      (getUserFullName(member)
        .toLowerCase()
        .includes(searchValue.toLowerCase()) ||
        member.email_address
          ?.toLowerCase()
          .includes(searchValue.toLowerCase())) &&
      !updatedMembers.find((m) => m.user_id === member.user_id),
  )

  const handleMemberSelect = (member: IFavoritedUser): void => {
    if (!updatedMembers.find((m) => m.user_id === member.user_id)) {
      if (updatedMembers.length >= 10) {
        return
      }
      setUpdatedMembers([...updatedMembers, member])
      setSearchValue('')
    }
  }

  const handleRemoveMember = (memberId: string): void => {
    setUpdatedMembers(updatedMembers.filter((m) => m.user_id !== memberId))
  }

  const handleClose = (): void => {
    setUpdatedMembers(currentFavorites)
    setSearchValue('')
    onRequestClose()
  }

  const handleSave = async (): Promise<void> => {
    const removedMembers = currentFavorites.filter(
      (current) =>
        !updatedMembers.find((updated) => current.user_id === updated.user_id),
    )
    const addedMembers = updatedMembers.filter(
      (updated) =>
        !currentFavorites.find(
          (current) => current.user_id === updated.user_id,
        ),
    )

    if (removedMembers.length === 0 && addedMembers.length === 0) {
      handleClose()
      return
    }

    try {
      showLoading({ container: EDIT_FAVORITES_MODAL_LOADING_CONTAINER })

      const deletePromise =
        removedMembers.length > 0
          ? dispatch(
              bulkDeleteUsers(
                Array.from(new Set(removedMembers.map((m) => m.user_id))),
              ),
            ).unwrap()
          : Promise.resolve()
      const addPromise =
        addedMembers.length > 0
          ? dispatch(
              addFavoritedUsers(
                Array.from(new Set(addedMembers.map((m) => m.user_id))),
              ),
            ).unwrap()
          : Promise.resolve()

      await Promise.all([deletePromise, addPromise])

      if (removedMembers.length > 0) {
        showToast(
          createSuccessToast(
            'Removed from favorites',
            `${removedMembers.length} member${removedMembers.length === 1 ? '' : 's'} removed from your favorites.`,
          ),
        )
      }
      if (addedMembers.length > 0) {
        showToast(
          createSuccessToast(
            'Added to favorites',
            `${addedMembers.length} member${addedMembers.length === 1 ? '' : 's'} added to your favorites.`,
          ),
        )
      }
      handleClose()
    } catch {
      showToast(
        createFailedToast(
          'Update failed',
          'Failed to update favorites. Please try again.',
        ),
      )
    } finally {
      hideLoading(EDIT_FAVORITES_MODAL_LOADING_CONTAINER)
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      className="edit-favorites-modal"
      backdrop="static"
    >
      <Modal.Header className="bg-custom3" closeButton>
        <Modal.Title>Edit favorites</Modal.Title>
      </Modal.Header>

      <Modal.Body className="edit-favorites-modal-body">
        <div className="edit-favorites-modal-content">
          <div className="edit-favorites-search-container">
            <input
              type="text"
              className="edit-favorites-search-input"
              placeholder="Enter the name or email of a team member"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {searchValue && filteredMembers.length > 0 && (
              <div className="edit-favorites-member-dropdown">
                {filteredMembers.map((member) => {
                  const isLimitReached = updatedMembers.length >= 10
                  return (
                    <div
                      key={member.user_id}
                      className={`edit-favorites-member-option ${isLimitReached ? 'edit-favorites-member-option-disabled' : ''}`}
                      onClick={() =>
                        !isLimitReached && handleMemberSelect(member)
                      }
                      style={{
                        cursor: isLimitReached ? 'not-allowed' : 'pointer',
                        opacity: isLimitReached ? 0.5 : 1,
                      }}
                    >
                      <Avatar
                        source={
                          member.provider_profile?.photo ??
                          'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                        }
                        alt={getFullName(member)}
                        width={32}
                        height={32}
                        style={{ borderRadius: '8px' }}
                      />
                      <div className="edit-favorites-member-info">
                        <div className="edit-favorites-member-name">
                          {getUserFullName(member)}
                        </div>
                        <div className="edit-favorites-member-email">
                          {member.email_address}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {updatedMembers.length > 0 && (
            <div className="edit-favorites-member-badges">
              <div className="favorite-count-limit">
                {updatedMembers.length}/10 favorites
                {updatedMembers.length >= 10 && (
                  <span style={{ color: 'var(--red-500)', marginLeft: '8px' }}>
                    (Limit reached)
                  </span>
                )}
              </div>
              {updatedMembers.map((member) => (
                <div
                  key={member.user_id}
                  className="edit-favorites-member-badge"
                >
                  <Avatar
                    source={
                      member.provider_profile?.photo ??
                      'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                    }
                    alt={getFullName(member)}
                    width={16}
                    height={16}
                    style={{ borderRadius: '6px' }}
                  />
                  <span className="edit-favorites-badge-name">
                    {getUserFullName(member)}
                  </span>
                  <button
                    type="button"
                    className="unstyled-button ti ti-x edit-favorites-badge-close"
                    aria-label="Remove member"
                    onClick={() => handleRemoveMember(member.user_id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="ms-auto">
          <Button
            size="md"
            className="button-secondary me-2"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button size="md" className="button-primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default EditFavoritesModal
